import { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PaperPlaneTilt, Plus, Play } from "@phosphor-icons/react";
import LoadFailedPanel from "@/components/LoadFailedPanel";

const DEFAULT_TOUCHES = [
  { day_offset: 0, subject: "Welcome — let's start the conversation", html_body: "<p>Hi {{name}},</p><p>Glad to connect.</p>" },
  { day_offset: 7, subject: "Quick check-in", html_body: "<p>Hi {{name}},</p><p>Following up.</p>" },
  { day_offset: 21, subject: "One last thought", html_body: "<p>Hi {{name}},</p><p>Closing the loop.</p>" },
];

export default function DripCampaigns() {
  const { user } = useAuth();
  const can = (user?.ux_tier || 0) >= 3;
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState({ name: "", tag: "", touches: DEFAULT_TOUCHES.map(t => ({ ...t })) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoadErr(null);
    try { const { data } = await api.get("/drip-campaigns"); setList(data.campaigns || []); }
    catch (e) { setLoadErr(e); }
  }, []);
  useEffect(() => { if (can) load(); }, [can, load]);

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.post("/drip-campaigns", editing);
      setEditing({ name: "", tag: "", touches: DEFAULT_TOUCHES.map(t => ({ ...t })) });
      await load(); setMsg("Campaign saved as draft.");
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const run = async (cid) => {
    if (!window.confirm("Launch this campaign? It will enroll all matching stakeholders and schedule the 3 sends.")) return;
    try {
      const { data } = await api.post(`/drip-campaigns/${cid}/run`);
      setMsg(`Launched. Enrolled ${data.enrolled}, scheduled ${data.scheduled_sends} sends. (Cron flushes every 15 min.)`);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Launch failed"); }
  };

  if (!can) return <div className="p-6 text-[var(--text-secondary)]">Drip campaigns require Tier 3+.</div>;
  if (loadErr) return <div className="p-6"><LoadFailedPanel error={loadErr} resource="Drip Campaigns" onRetry={load} /></div>;

  return (
    <div data-testid="drip-page" className="p-6 max-w-6xl space-y-6">
      <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
        <PaperPlaneTilt size={28} className="text-[#F47D20]" />
        Drip Campaigns
      </h1>
      <p className="text-xs text-[var(--text-muted)]">3-touch sequence on a stakeholder tag (default D0/D7/D21). Cron flushes pending sends every 15 min.</p>

      <section data-testid="drip-builder" className="bg-white border border-[var(--border)] rounded-lg p-5 space-y-3">
        <div className="text-sm font-bold text-[#263776]">New campaign</div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="drip-name" placeholder="Campaign name" value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})}
            className="border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
          <input data-testid="drip-tag" placeholder="Tag to enroll (e.g., vip)" value={editing.tag} onChange={e => setEditing({...editing, tag: e.target.value})}
            className="border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
        </div>
        {editing.touches.map((t, i) => (
          <div key={i} data-testid={`drip-touch-${i}`} className="bg-[var(--surface-2)] rounded p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-secondary)]">
              <span>Touch #{i+1}</span>
              <span className="text-[var(--text-faint)]">Day</span>
              <input data-testid={`drip-touch-${i}-day`} type="number" min={0} value={t.day_offset} onChange={e => {
                const arr = [...editing.touches]; arr[i] = {...arr[i], day_offset: parseInt(e.target.value || 0)}; setEditing({...editing, touches: arr});
              }} className="w-16 border border-[var(--border-strong)] rounded px-2 py-1 text-xs" />
            </div>
            <input data-testid={`drip-touch-${i}-subject`} placeholder="Subject line" value={t.subject} onChange={e => {
              const arr = [...editing.touches]; arr[i] = {...arr[i], subject: e.target.value}; setEditing({...editing, touches: arr});
            }} className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
            <textarea data-testid={`drip-touch-${i}-body`} placeholder="HTML body (supports {{name}} placeholder)" rows={3} value={t.html_body} onChange={e => {
              const arr = [...editing.touches]; arr[i] = {...arr[i], html_body: e.target.value}; setEditing({...editing, touches: arr});
            }} className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-xs font-mono" />
          </div>
        ))}
        <button data-testid="drip-save" onClick={save} disabled={busy || !editing.name || !editing.tag}
          className="bg-[#F47D20] text-white px-4 py-2 text-sm rounded hover:bg-[#c96009] disabled:opacity-50 flex items-center gap-2">
          <Plus weight="bold" size={14} /> Save as draft
        </button>
        {msg && <div data-testid="drip-msg" className="text-emerald-600 text-xs">{msg}</div>}
        {err && <div data-testid="drip-err" className="text-rose-600 text-xs">{err}</div>}
      </section>

      <section data-testid="drip-list" className="bg-white border border-[var(--border)] rounded-lg overflow-hidden">
        <div className="text-[11px] uppercase tracking-wider text-[var(--text-faint)] px-5 py-3 border-b border-[var(--border)]">Existing campaigns</div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)] text-[var(--text-secondary)] text-xs">
            <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Tag</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Enrolled</th><th className="text-left px-3 py-2">Sent</th><th className="text-right px-3 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-[var(--text-faint)] text-xs">No campaigns yet.</td></tr>}
            {list.map(c => (
              <tr key={c.campaign_id} data-testid={`drip-row-${c.campaign_id}`} className="border-t border-[var(--border)]">
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2"><code className="text-[11px] bg-[var(--surface-3)] px-2 py-0.5 rounded">{c.tag}</code></td>
                <td className="px-3 py-2 text-xs capitalize">{c.status}</td>
                <td className="px-3 py-2 text-xs">{c.enrolled_count || 0}</td>
                <td className="px-3 py-2 text-xs">{c.sent_count || 0}</td>
                <td className="px-3 py-2 text-right">
                  {c.status === "draft" && (
                    <button data-testid={`drip-run-${c.campaign_id}`} onClick={() => run(c.campaign_id)}
                      className="text-xs bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700 inline-flex items-center gap-1">
                      <Play size={12} weight="fill" /> Launch
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
